import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import { Drawer } from "@material-ui/core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportIcon from "@mui/icons-material/Report";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "./sidebar.css";
import logo from "../../assets/images/logo.png";
import SidebarItem from "./SidebarItem";
import {
  Dashboard,
  Category,
  Phone,
  People,
  Settings,
  ShoppingCart,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SettingsIcon from "@mui/icons-material/Settings";
import { isAuth, profilePicture } from "../../actions/auth";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Image_API } from "../../config";
import InventoryIcon from '@mui/icons-material/Inventory';

const drawerWidth = 270;

const admin_routes = [
  {
    display_name: "Dashboard",
    route: "/",
    icon: <Dashboard />,
  },
  {
    display_name: "Purchase Orders",
    route: "/purchase-order",
    icon: <ShoppingCart />,
  },
  {
    display_name: "Inventories",
    icon: <InventoryIcon />,
    subRoutes: [
      {
        display_name: "Categories",
        route: "/categories",
      },
      {
        display_name: "Inventories",
        route: "/products",
      },
      {
        display_name: "Containers",
        route: "/containers",
      },
    ],
  },
  {
    display_name: "Customers",
    route: "/customers",
    icon: <PersonAddAlt1Icon />,
  },
  {
    display_name: "Reports",
    icon: <ReportIcon />,
    subRoutes: [
      {
        display_name: "Sales Report",
        route: "/sales-reports",
      },
      {
        display_name: "Sales Detail Report",
        route: "/detail-reports",
      },
      {
        display_name: "Inventory Report",
        route: "/inventory",
      },
      {
        display_name: "Inventory Report By Container",
        route: "/container-report",
      },
      {
        display_name: "Activity Log Report",
        route: "/activity-log",
      },
    ],
  },
];
const superadmin_routes = [
  {
    display_name: "Dashboard",
    route: "/admin",
    icon: <Dashboard />,
  },
  {
    display_name: "Organization",
    route: "/admin/organization",
    icon: <SettingsIcon />,
  },
  {
    display_name: "Organization admin",
    route: "/admin/staff",
    icon: <People />,
  },
  {
    display_name: "Register",
    route: "/admin/signup",
    icon: <PersonAddIcon/>,
  }
];

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  borderR: {
    borderRight: "none",
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

const Sidebar = (props) => {
  const history = useHistory();
  const classes = useStyles();
  const location = useLocation();
  const [open, setOpen] = useState({});
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    const authData = isAuth();
    let newAdminRoutes = [...admin_routes];

    if (authData.staffRoles) {
      const rolePermissions = authData.staffRoles[0]?.role?.rolePermissions || [];
      const hasManageStaffPermission = rolePermissions.some(
        (permission) => permission.action === "manageStaff"
      );
      const hasGeneralSettingPermission = rolePermissions.some(
        (permission) => permission.action === "generalSetting"
      );
      const hasStoreKeeperPermission = rolePermissions.some(
        (permission) => permission.action === "onlyMineShipment" || permission.action === "viewAllShipment"
      );
      const hasSalesPermission = rolePermissions.some(
        (permission) => permission.action === "addOrder" || permission.action === "viewOrders"
      );
      const hasSalesReportPermission = rolePermissions.some(
        (permission) => permission.action === "salesReport"
      );
      const hasActivityReportPermission = rolePermissions.some(
        (permission) => permission.action === "activityReport"
      );
      const hasInventoryReportPermission = rolePermissions.some(
        (permission) => permission.action === "inventoryReport"
      );
      const hasContainerReportPermission = rolePermissions.some(
        (permission) => permission.action === "inventoryReport"
      );
      const hasManageProductsPermission = rolePermissions.some(
        (permission) => permission.action === "manageProducts"
      );

      // Add specific routes based on permissions
      if (hasManageStaffPermission || authData.role === "admin") {
        const staffsIndex = newAdminRoutes.findIndex(
          (route) => route.display_name === "Staffs"
        );
        if (staffsIndex === -1) {
          newAdminRoutes.splice(3, 0, {
            display_name: "Staffs",
            icon: <People />,
            subRoutes: [
              { display_name: "Roles", route: "/roles" },
              { display_name: "Staffs", route: "/staffs" },
            ],
          });
        }
      }
      if (hasGeneralSettingPermission || authData.role === "admin") {
        const generalSettingsIndex = newAdminRoutes.findIndex(
          (route) => route.display_name === "General Settings"
        );
        if (generalSettingsIndex === -1) {
          newAdminRoutes.splice(6, 0, {
            display_name: "General Settings",
            route: "/settings",
            icon: <SettingsIcon />,
          });
        }
      }
      if (hasStoreKeeperPermission || authData.role === "admin") {
        const shipmentIndex = newAdminRoutes.findIndex(
          (route) => route.display_name === "Shipment"
        );
        if (shipmentIndex === -1) {
          newAdminRoutes.splice(5, 0, {
            display_name: "Shipment",
            route: "/shipment-order",
            icon: <ShoppingCart />,
          });
        }
      }
      if (hasSalesPermission || authData.role === "admin") {
        const salesIndex = newAdminRoutes.findIndex(
          (route) => route.display_name === "Sales Order"
        );
        if (salesIndex === -1) {
          newAdminRoutes.splice(4, 0, {
            display_name: "Sales Order",
            route: "/orders",
            icon: <ShoppingCartIcon />,
          });
        }
      }
      if (hasManageProductsPermission || authData.role === "admin") {
        const purchaseOrderIndex = newAdminRoutes.findIndex(
          (route) => route.display_name === "Purchase Orders" && route.route === "/purchase-order"
        );
        if (purchaseOrderIndex === -1) {
          newAdminRoutes.splice(1, 0, {
            display_name: "Purchase Orders",
            route: "/purchase-order",
            icon: <ShoppingCart />,
          });
        }
      }

      const reportsIndex = newAdminRoutes.findIndex(
        (route) => route.display_name === "Reports"
      );
      if (reportsIndex !== -1) {
        const reportSubRoutes = [];
        if (hasSalesReportPermission || authData.role === "admin") {
          reportSubRoutes.push(
            { display_name: "Sales Report", route: "/sales-reports" },
            { display_name: "Sales Detail Report", route: "/detail-reports" }
          );
        }
        if (hasInventoryReportPermission || authData.role === "admin") {
          reportSubRoutes.push({ display_name: "Inventory Report", route: "/inventory" });
        }
        if (hasContainerReportPermission || authData.role === "admin") {
          reportSubRoutes.push({ display_name: "Inventory Report By Container", route: "/container-report" });
        }
        if (hasContainerReportPermission || authData.role === "admin") {
          reportSubRoutes.push({ display_name: "Payment Report", route: "/payment-report" });
        }
        if (hasContainerReportPermission || authData.role === "admin") {
          reportSubRoutes.push({ display_name: "Purchase Order Report", route: "/purchase-container" });
        }
        if (hasActivityReportPermission || authData.role === "admin") {
          reportSubRoutes.push({ display_name: "Activity Log Report", route: "/activity-log" });
        }

        if (reportSubRoutes.length > 0) {
          newAdminRoutes[reportsIndex].subRoutes = reportSubRoutes;
        } else {
          newAdminRoutes.splice(reportsIndex, 1);
        }
      }

      const finalReportsIndex = newAdminRoutes.findIndex(
        (route) => route.display_name === "Reports"
      );
      if (finalReportsIndex !== -1) {
        const [reportsItem] = newAdminRoutes.splice(finalReportsIndex, 1);
        newAdminRoutes.push(reportsItem); // add "Reports" at the end
      }
    }

    if (location.pathname.includes("/admin")) {
      newAdminRoutes = [...superadmin_routes];
    }
    const order = [
      "Dashboard",
      "Purchase Orders",
      "Inventories",
      "Sales Order",
      "Customers",
      "Shipment",
      "Staffs",
      "General Settings",
      "Reports"
    ];
    if (location.pathname.includes("/admin")) {
      setRoutes(newAdminRoutes);
    } else {
      const orderedRoutes = order
        .map((routeName) => {
          return newAdminRoutes.find(route => route.display_name === routeName);
        })
        .filter(Boolean);
      setRoutes(orderedRoutes);
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    routes.forEach((item, index) => {
      if (item.subRoutes) {
        item.subRoutes.forEach((subItem) => {
          if (currentPath.includes(subItem.route)) {
            setOpen((prevOpen) => ({ ...prevOpen, [index]: true }));
          }
        });
      }
    });
  }, [location.pathname, routes]);

  const handleToggle = (index) => {
    setOpen((prevOpen) => ({ ...prevOpen, [index]: !prevOpen[index] }));
  };

  const handleMenuItemClick = (route) => {
    if (window.innerWidth <= 768) {
      props.handleClose();
    }
    history.push(route);
  };

  const activeItem = routes.findIndex(
    (item) => item.route === location.pathname
  );

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="left"
      open={props.open}
      classes={{
        paperAnchorDockedLeft: classes.borderR,
        paper: classes.drawerPaper,
      }}
    >
      <div className="sidebar">
        <div className="sidebar__logo">
          {profilePicture() ?
            <img src={`${Image_API}/${profilePicture()}`} alt="company logo" /> :
            <img src={logo} alt="company logo" />
          }
        </div>
        {routes.map((item, index) => (
          <React.Fragment key={index}>
            {item.subRoutes ? (
              <>
                <div
                  className={`sidebar__item-inner expandable ${
                    open[index] ? "open" : ""
                  }`}
                  onClick={() => handleToggle(index)}
                >
                  {item.icon && <i>{item.icon}</i>}
                  <span>{item.display_name}</span>
                  {open[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </div>
                {open[index] && (
                  <div className="sidebar__sublist">
                    {item.subRoutes.map((subItem, subIndex) => (
                      <div key={subIndex} onClick={() => handleMenuItemClick(subItem.route)}>
                        <SidebarItem
                          title={subItem.display_name}
                          icon={subItem.icon}
                          active={location.pathname === subItem.route}
                          className={classes.nested}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div key={index} onClick={() => handleMenuItemClick(item.route)}>
                <SidebarItem
                  title={item.display_name}
                  icon={item.icon}
                  active={index === activeItem}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Drawer>
  );
};

export default Sidebar;